import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, Plus, Trash2, Pencil, Youtube, FileText, ExternalLink, TriangleAlert } from 'lucide-react';
import {
  AcademicClassesApi,
  CreateLessonFileData,
  LessonFile,
  LessonListRow,
  LessonsApi,
  LessonTopicListRow,
  LessonTopicsApi,
} from '@/services/academic.api';
import { UploadApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { DataTable, useDataTable } from '@/components/data-table';
import { usePermissions } from '@/hooks/usePermissions';

const columnHelper = createColumnHelper<LessonListRow>();

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

// ───── Shared class → section → subject cascading picker ────────────────────
function ClassSectionSubjectPicker({
  classId,
  onClassChange,
  sectionId,
  onSectionChange,
  classSubjectId,
  onSubjectChange,
}: {
  classId: string;
  onClassChange: (v: string) => void;
  sectionId: string;
  onSectionChange: (v: string) => void;
  classSubjectId: string;
  onSubjectChange: (v: string) => void;
}) {
  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-lessons-picker'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', classId],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  const subjects = useMemo(() => {
    const seen = new Map<string, { classSubjectId: string; subjectName: string }>();
    for (const r of subjectRows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, { classSubjectId: r.classSubjectId, subjectName: r.subjectName });
    return [...seen.values()];
  }, [subjectRows]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Class" required>
        <Select value={classId} onChange={(e) => onClassChange(e.target.value)}>
          <option value="">Select class</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Section" required>
        <Select value={sectionId} onChange={(e) => onSectionChange(e.target.value)} disabled={!classId}>
          <option value="">Select section</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Subject" required>
        <Select value={classSubjectId} onChange={(e) => onSubjectChange(e.target.value)} disabled={!classId}>
          <option value="">Select subject</option>
          {subjects.map((s) => (
            <option key={s.classSubjectId} value={s.classSubjectId}>
              {s.subjectName}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

// ───── Shared file attach/list widget (lesson-level and topic-level reuse this) ─
function FilesManager({
  files,
  onAdd,
  adding,
  addError,
  onRemove,
  removingId,
}: {
  files: LessonFile[];
  onAdd: (data: CreateLessonFileData) => void;
  adding: boolean;
  addError?: unknown;
  onRemove: (fileId: string) => void;
  removingId?: string;
}) {
  const [fileType, setFileType] = useState<'document' | 'youtube'>('document');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [title, setTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  async function handleDocumentPick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await UploadApi.upload(file);
      onAdd({ fileType: 'document', fileUrl: res.url, title: title || res.name });
      setTitle('');
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  function handleAddYoutube() {
    if (!youtubeUrl.trim()) return;
    onAdd({ fileType: 'youtube', youtubeUrl: youtubeUrl.trim(), title: title || undefined });
    setYoutubeUrl('');
    setTitle('');
  }

  return (
    <div>
      {files.length === 0 ? (
        <p className="mb-2 text-xs text-slate-400">No files attached yet.</p>
      ) : (
        <ul className="mb-3 divide-y divide-slate-100">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between py-1.5">
              {f.note ? (
                <span className="flex items-center gap-2 text-sm text-amber-700" title={f.note}>
                  <TriangleAlert className="h-4 w-4 shrink-0" />
                  {f.title || 'Attachment'}
                  <span className="text-xs text-amber-600">— {f.note}</span>
                </span>
              ) : (
                <a
                  href={f.fileType === 'youtube' ? f.youtubeUrl ?? '#' : f.fileUrl ?? '#'}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 text-sm text-brand-600 hover:underline"
                >
                  {f.fileType === 'youtube' ? <Youtube className="h-4 w-4 shrink-0" /> : <FileText className="h-4 w-4 shrink-0" />}
                  {f.title || (f.fileType === 'youtube' ? 'YouTube link' : 'Document')}
                  <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
                </a>
              )}
              <button
                type="button"
                title="Remove"
                className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                disabled={removingId === f.id}
                onClick={() => onRemove(f.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-lg border border-dashed border-slate-300 p-3">
        <div className="mb-2 flex gap-1">
          <button
            type="button"
            className={`rounded px-2 py-1 text-xs ${fileType === 'document' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setFileType('document')}
          >
            Document
          </button>
          <button
            type="button"
            className={`rounded px-2 py-1 text-xs ${fileType === 'youtube' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-600'}`}
            onClick={() => setFileType('youtube')}
          >
            YouTube link
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="!w-40 !py-1 text-sm"
          />
          {fileType === 'document' ? (
            <label className="btn-secondary cursor-pointer !py-1 text-xs">
              {uploading ? 'Uploading…' : 'Choose file'}
              <input
                type="file"
                className="hidden"
                disabled={uploading || adding}
                onChange={(e) => e.target.files?.[0] && handleDocumentPick(e.target.files[0])}
              />
            </label>
          ) : (
            <>
              <Input
                placeholder="https://youtube.com/watch?v=…"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                className="!w-64 !py-1 text-sm"
              />
              <button
                type="button"
                className="btn-secondary flex items-center gap-1 !py-1 text-xs"
                disabled={!youtubeUrl.trim() || adding}
                onClick={handleAddYoutube}
              >
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </>
          )}
        </div>
        {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
        {addError ? <p className="mt-1 text-xs text-red-600">{errMsg(addError)}</p> : null}
      </div>
    </div>
  );
}

// ───── Topics nested under a lesson ──────────────────────────────────────────
function TopicFiles({ topicId }: { topicId: string }) {
  const qc = useQueryClient();
  const { data: detail } = useQuery({
    queryKey: ['lesson-topics', topicId, 'detail'],
    queryFn: () => LessonTopicsApi.get(topicId),
  });

  const addFile = useMutation({
    mutationFn: (data: CreateLessonFileData) => LessonTopicsApi.addFile(topicId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lesson-topics', topicId, 'detail'] }),
  });
  const [removingId, setRemovingId] = useState<string | undefined>();
  const removeFile = useMutation({
    mutationFn: (fileId: string) => LessonTopicsApi.removeFile(topicId, fileId),
    onMutate: (fileId) => setRemovingId(fileId),
    onSettled: () => setRemovingId(undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lesson-topics', topicId, 'detail'] }),
  });

  return (
    <FilesManager
      files={detail?.files ?? []}
      onAdd={(d) => addFile.mutate(d)}
      adding={addFile.isPending}
      addError={addFile.error}
      onRemove={(id) => removeFile.mutate(id)}
      removingId={removingId}
    />
  );
}

function CreateTopicForm({
  lessonId,
  defaultSectionId,
  defaultClassSubjectId,
  defaultClassId,
  onDone,
}: {
  lessonId: string;
  defaultSectionId: string;
  defaultClassSubjectId: string;
  defaultClassId: string;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [classId, setClassId] = useState(defaultClassId);
  const [sectionId, setSectionId] = useState(defaultSectionId);
  const [classSubjectId, setClassSubjectId] = useState(defaultClassSubjectId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    setSectionId('');
    setClassSubjectId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const create = useMutation({
    mutationFn: () => LessonTopicsApi.create({ lessonId, sectionId, classSubjectId, name, description: description || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lesson-topics', lessonId] });
      qc.invalidateQueries({ queryKey: ['lessons'] });
      onDone();
    },
  });

  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-3">
      <ClassSectionSubjectPicker
        classId={classId}
        onClassChange={setClassId}
        sectionId={sectionId}
        onSectionChange={setSectionId}
        classSubjectId={classSubjectId}
        onSubjectChange={setClassSubjectId}
      />
      <Field label="Topic name" required className="mt-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Introduction" />
      </Field>
      <Field label="Description" className="mt-3">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </Field>
      {create.error && <p className="mt-2 text-xs text-red-600">{errMsg(create.error)}</p>}
      <div className="mt-3 flex gap-2">
        <button className="btn-secondary !py-1 text-xs" onClick={onDone}>
          Cancel
        </button>
        <button
          className="btn-primary !py-1 text-xs"
          disabled={!sectionId || !classSubjectId || !name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create Topic'}
        </button>
      </div>
    </div>
  );
}

function TopicRow({ topic, onDelete }: { topic: LessonTopicListRow; onDelete: () => void }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <button type="button" className="text-left text-sm font-medium text-slate-900 hover:text-brand-600" onClick={() => setExpanded((v) => !v)}>
          {topic.name}
        </button>
        <div className="flex items-center gap-2">
          <Badge tone="slate">{topic.fileCount} file{topic.fileCount === 1 ? '' : 's'}</Badge>
          <button type="button" className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={onDelete} title="Delete topic">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {topic.description && <p className="mt-1 text-xs text-slate-500">{topic.description}</p>}
      {expanded && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <TopicFiles topicId={topic.id} />
        </div>
      )}
    </div>
  );
}

function TopicsManager({ lesson }: { lesson: LessonListRow }) {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<LessonTopicListRow | null>(null);

  const { data } = useQuery({
    queryKey: ['lesson-topics', lesson.id],
    queryFn: () => LessonTopicsApi.list({ lessonId: lesson.id, limit: 100 }),
  });
  const topics = data?.items ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => LessonTopicsApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['lesson-topics', lesson.id] });
      qc.invalidateQueries({ queryKey: ['lessons'] });
    },
  });

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Topics</h4>
        {!creating && (
          <button type="button" className="btn-secondary flex items-center gap-1 !py-1 text-xs" onClick={() => setCreating(true)}>
            <Plus className="h-3.5 w-3.5" /> Add Topic
          </button>
        )}
      </div>

      <div className="space-y-2">
        {topics.map((t) => (
          <TopicRow key={t.id} topic={t} onDelete={() => setDeleting(t)} />
        ))}
        {topics.length === 0 && !creating && <p className="text-xs text-slate-400">No topics yet.</p>}
      </div>

      {creating && (
        <div className="mt-3">
          <CreateTopicForm
            lessonId={lesson.id}
            defaultClassId={lesson.classId}
            defaultSectionId={lesson.sectionId}
            defaultClassSubjectId={lesson.classSubjectId}
            onDone={() => setCreating(false)}
          />
        </div>
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!.id)}
        title="Delete topic?"
        message={remove.error ? errMsg(remove.error) : `This permanently deletes "${deleting?.name}" and its attached files. This cannot be undone.`}
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}

// ───── Lesson detail modal (files + topics) ──────────────────────────────────
function LessonDetail({ lessonId }: { lessonId: string }) {
  const qc = useQueryClient();
  const { data: detail } = useQuery({
    queryKey: ['lessons', lessonId, 'detail'],
    queryFn: () => LessonsApi.get(lessonId),
  });

  const addFile = useMutation({
    mutationFn: (data: CreateLessonFileData) => LessonsApi.addFile(lessonId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons', lessonId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
  const [removingId, setRemovingId] = useState<string | undefined>();
  const removeFile = useMutation({
    mutationFn: (fileId: string) => LessonsApi.removeFile(lessonId, fileId),
    onMutate: (fileId) => setRemovingId(fileId),
    onSettled: () => setRemovingId(undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons', lessonId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['lessons'] });
    },
  });

  if (!detail) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge tone="blue">{detail.className}</Badge>
        <Badge tone="slate">{detail.sectionName}</Badge>
        <Badge tone="indigo">{detail.subjectName}</Badge>
        {detail.createdByName && <span>Created by {detail.createdByName}</span>}
      </div>
      {detail.description && <p className="text-sm text-slate-700">{detail.description}</p>}

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Files</h4>
        <FilesManager
          files={detail.files}
          onAdd={(d) => addFile.mutate(d)}
          adding={addFile.isPending}
          addError={addFile.error}
          onRemove={(id) => removeFile.mutate(id)}
          removingId={removingId}
        />
      </div>

      <TopicsManager lesson={detail} />
    </div>
  );
}

// ───── Create lesson form ─────────────────────────────────────────────────────
function CreateLessonForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [classSubjectId, setClassSubjectId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    setSectionId('');
    setClassSubjectId('');
  }, [classId]);

  const create = useMutation({
    mutationFn: () => LessonsApi.create({ sectionId, classSubjectId, name, description: description || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons'] });
      onDone();
    },
  });

  return (
    <div>
      <ClassSectionSubjectPicker
        classId={classId}
        onClassChange={setClassId}
        sectionId={sectionId}
        onSectionChange={setSectionId}
        classSubjectId={classSubjectId}
        onSubjectChange={setClassSubjectId}
      />
      <Field label="Lesson name" required className="mt-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chapter 3 — Photosynthesis" />
      </Field>
      <Field label="Description" className="mt-3">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </Field>
      {create.error && <p className="mt-2 text-xs text-red-600">{errMsg(create.error)}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button
          className="btn-primary"
          disabled={!sectionId || !classSubjectId || !name.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create Lesson'}
        </button>
      </div>
    </div>
  );
}

// ───── Edit lesson form ───────────────────────────────────────────────────────
function EditLessonForm({ lesson, onDone }: { lesson: LessonListRow; onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(lesson.name);
  const [description, setDescription] = useState(lesson.description ?? '');

  const update = useMutation({
    mutationFn: () => LessonsApi.update(lesson.id, { name, description: description || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lessons'] });
      onDone();
    },
  });

  return (
    <div>
      <Field label="Lesson name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Description" className="mt-3">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </Field>
      {update.error && <p className="mt-2 text-xs text-red-600">{errMsg(update.error)}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!name.trim() || update.isPending} onClick={() => update.mutate()}>
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ───── Page ────────────────────────────────────────────────────────────────
export function LessonsPage() {
  const { can } = usePermissions();
  const canWrite = can('/setup/lessons', 'create');
  const canDelete = can('/setup/lessons', 'delete');

  const qc = useQueryClient();
  const dt = useDataTable({ defaultLimit: 20 });
  const [creating, setCreating] = useState(false);
  const [managingId, setManagingId] = useState<string | null>(null);
  const [editing, setEditing] = useState<LessonListRow | null>(null);
  const [deleting, setDeleting] = useState<LessonListRow | null>(null);

  const { data, isFetching, error } = useQuery({
    queryKey: ['lessons', dt.params],
    queryFn: () => LessonsApi.list({ page: dt.params.page, limit: dt.params.limit }),
    placeholderData: keepPreviousData,
  });

  const remove = useMutation({
    mutationFn: (id: string) => LessonsApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['lessons'] });
    },
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('name', {
      header: 'Lesson',
      cell: (info) => (
        <button type="button" className="font-medium text-brand-600 hover:underline" onClick={() => setManagingId(info.row.original.id)}>
          {info.getValue()}
        </button>
      ),
    }),
    columnHelper.accessor('className', { header: 'Class' }),
    columnHelper.accessor('sectionName', { header: 'Section' }),
    columnHelper.accessor('subjectName', { header: 'Subject' }),
    columnHelper.accessor('fileCount', { header: 'Files' }),
    columnHelper.accessor('topicCount', { header: 'Topics' }),
    columnHelper.accessor('createdByName', {
      header: 'Created By',
      cell: (info) => info.getValue() ?? <span className="text-slate-400">—</span>,
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (info) => (
        <span className="inline-flex items-center gap-2">
          {canWrite && (
            <button
              type="button"
              title="Edit"
              className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700"
              onClick={() => setEditing(info.row.original)}
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              title="Delete"
              className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => setDeleting(info.row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </span>
      ),
    }),
  ];

  return (
    <div>
      <PageHeader
        title="Lessons & Topics"
        description="Author lessons for a class/section/subject, attach documents or YouTube links, and break each into topics."
        actions={
          canWrite ? (
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create Lesson
            </button>
          ) : undefined
        }
      />

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{errMsg(error)}</div>
      ) : (
        <DataTable
          columns={columns}
          result={data}
          state={dt}
          loading={isFetching}
          emptyMessage="No lessons created yet."
        />
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Lesson" size="lg">
        <CreateLessonForm onDone={() => setCreating(false)} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Lesson" size="md">
        {editing && <EditLessonForm lesson={editing} onDone={() => setEditing(null)} />}
      </Modal>

      <Modal
        open={!!managingId}
        onClose={() => setManagingId(null)}
        title={data?.items.find((l) => l.id === managingId)?.name ?? 'Lesson'}
        size="lg"
      >
        {managingId && <LessonDetail lessonId={managingId} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!.id)}
        title="Delete lesson?"
        message={
          remove.error
            ? errMsg(remove.error)
            : `This permanently deletes "${deleting?.name}", all its topics, and every attached file. This cannot be undone.`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}
